import axios from 'axios';

const REACT_APP_API_URL_DN = process.env.REACT_APP_API_URL_DN || 'https://localhost:7111/api';

interface PartitionGenerationRequest {
  year: number;
}

interface PartitionGenerationResult {
  year: number;
  totalPartitions: number;
  sqlScript: string;
  partitionNames: string[];
  success: boolean;
  errorMessage?: string;
}

interface PartitionError {
  partitionName: string;
  message: string;
  oracleErrorCode?: string;
}

interface PartitionCreationResult {
  year: number;
  totalRegions: number;
  createdPartitions: number;
  skippedPartitions: number;
  failedPartitions: number;
  createdPartitionNames: string[];
  skippedPartitionNames: string[];
  errors: PartitionError[];
  success: boolean;
  errorMessage?: string;
}

const partitionService = {
  /**
   * Vygeneruje SQL skript pre vytvorenie databázových partícií pre zadaný rok
   * Identické správanie ako desktop aplikácia (tlačidlo "Create database partitions")
   * @param year - Rok pre ktorý sa majú vytvoriť partície (napr. 2027)
   * @returns Promise s výsledkom generovania
   */
  generatePartitionSQL: async (year: number): Promise<PartitionGenerationResult> => {
    try {
      const request: PartitionGenerationRequest = { year };
      
      const response = await axios.post<PartitionGenerationResult>(
        `${REACT_APP_API_URL_DN}/Partition/generate`,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      // Handle error response
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Nepodarilo sa vygenerovať SQL skript pre partície'
      );
    }
  },

  /**
   * Vytvorí partície pre zadaný rok priamo v databáze
   * NOVÁ FUNKCIONALITA - desktop aplikácia toto nerobí automaticky
   * @param year - Rok pre ktorý sa majú vytvoriť partície (napr. 2027)
   * @returns Promise s výsledkom vytvárania partícií
   */
  createPartitions: async (year: number): Promise<PartitionCreationResult> => {
    try {
      const request: PartitionGenerationRequest = { year };
      
      const response = await axios.post<PartitionCreationResult>(
        `${REACT_APP_API_URL_DN}/Partition/create`,
        request,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      // Handle error response
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Nepodarilo sa vytvoriť partície v databáze'
      );
    }
  },

  /**
   * Stiahne SQL skript ako .sql súbor
   * Identické správanie ako desktop aplikácia (uloží CreatePartitions_{year}.sql)
   * @param year - Rok pre ktorý sa majú vytvoriť partície
   * @returns Promise s blob súborom
   */
  downloadPartitionSQL: async (year: number): Promise<void> => {
    try {
      const response = await axios.get(
        `${REACT_APP_API_URL_DN}/Partition/download/${year}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CreatePartitions_${year}.sql`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Nepodarilo sa stiahnuť SQL skript'
      );
    }
  },

  /**
   * Health check endpoint
   */
  checkHealth: async (): Promise<boolean> => {
    try {
      const response = await axios.get(`${REACT_APP_API_URL_DN}/Partition/health`);
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }
};

export default partitionService;
